import React, { useEffect, useState, useCallback } from "react";
import {
    ledgerSignup,
    ledgerGet,
    ledgerCheckin,
    ledgerShare,
    ledgerLeaderboard,
} from "../lib/api";
import { Crown } from "./CrownIcon";
import { Trophy, Share2, CalendarCheck, Sparkles, LogOut } from "lucide-react";
import { toast } from "sonner";

const FAN_KEY = "gsb_fan_id";
const TIP_SHARE_URL = "https://kick.com/leonardthepreacher";

const isToday = (iso) => iso === new Date().toISOString().slice(0, 10);

export const LedgerSection = () => {
    const [fan, setFan] = useState(null);
    const [handle, setHandle] = useState("");
    const [board, setBoard] = useState([]);
    const [busy, setBusy] = useState(null);

    const loadBoard = useCallback(async () => {
        try {
            const data = await ledgerLeaderboard();
            setBoard(data);
        } catch {
            /* ignore */
        }
    }, []);

    useEffect(() => {
        loadBoard();
        const fanId = localStorage.getItem(FAN_KEY);
        if (fanId) {
            ledgerGet(fanId)
                .then(setFan)
                .catch(() => localStorage.removeItem(FAN_KEY));
        }
    }, [loadBoard]);

    const join = async (e) => {
        e.preventDefault();
        const h = handle.trim();
        if (h.length < 2) {
            toast.error("Handle must be 2+ characters");
            return;
        }
        setBusy("join");
        try {
            const f = await ledgerSignup(h);
            localStorage.setItem(FAN_KEY, f.id);
            setFan(f);
            await loadBoard();
            toast.success(`Welcome to the ledger, ${f.handle} 👑`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't join");
        } finally {
            setBusy(null);
        }
    };

    const doCheckin = async () => {
        if (!fan) return;
        setBusy("checkin");
        try {
            const f = await ledgerCheckin(fan.id);
            setFan(f);
            await loadBoard();
            toast.success("+10 pts · daily check-in claimed 🙌");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't check in");
        } finally {
            setBusy(null);
        }
    };

    const doShare = async () => {
        if (!fan) return;
        setBusy("share");
        try {
            // Open share window first (better UX), then claim points
            try {
                if (navigator.share) {
                    await navigator.share({
                        title: "Get Saved Bro · Leonard the Preacher",
                        text: "Pull up to the stream — 12AM EST every night.",
                        url: TIP_SHARE_URL,
                    });
                } else {
                    await navigator.clipboard.writeText(TIP_SHARE_URL);
                    toast.message("Stream link copied to clipboard");
                }
            } catch {
                /* user cancelled share, still claim */
            }
            const f = await ledgerShare(fan.id);
            setFan(f);
            await loadBoard();
            toast.success("+25 pts · share claimed 📣");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't share");
        } finally {
            setBusy(null);
        }
    };

    const logout = () => {
        localStorage.removeItem(FAN_KEY);
        setFan(null);
        setHandle("");
        toast.message("Signed out of the ledger");
    };

    const checkinDone = fan && isToday(fan.last_checkin);
    const shareDone = fan && isToday(fan.last_share);

    return (
        <section
            id="ledger"
            data-testid="ledger-section"
            className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24"
        >
            <div
                aria-hidden
                className="absolute inset-0 overflow-hidden pointer-events-none"
            >
                <div className="absolute top-0 right-1/4 w-96 h-96 rounded-full bg-kickgreen/15 blur-3xl" />
                <div className="absolute bottom-0 left-1/4 w-96 h-96 rounded-full bg-hotpink/15 blur-3xl" />
            </div>

            <div className="relative mb-10">
                <div className="inline-flex items-center gap-2 mb-3">
                    <Crown className="w-9 h-7" color="#53FC18" />
                    <span className="font-anton uppercase tracking-[0.3em] text-xs text-kickgreen">
                        The Preacher's Ledger
                    </span>
                </div>
                <h2
                    data-testid="ledger-title"
                    className="font-anton uppercase text-5xl md:text-7xl text-white leading-[0.9] stamp-sm"
                >
                    Stack <span className="text-hotpink">points</span>.
                    <br />
                    Climb the <span className="text-skyblue">ranks</span>.
                </h2>
                <p className="font-graffiti text-kickgreen text-2xl -rotate-2 mt-2">
                    engage ✦ share ✦ rise
                </p>
            </div>

            <div className="relative grid grid-cols-1 lg:grid-cols-5 gap-8">
                {/* Dashboard */}
                <div className="lg:col-span-3 space-y-5">
                    {!fan ? (
                        <form
                            data-testid="ledger-join-form"
                            onSubmit={join}
                            className="bg-surface border-4 border-black brutal-shadow-pink p-6 md:p-8 space-y-4"
                        >
                            <div className="flex items-center gap-2 mb-1">
                                <Sparkles className="w-5 h-5 text-kickgreen" />
                                <span className="font-anton uppercase tracking-widest text-xs text-white/60">
                                    Join the Ledger
                                </span>
                            </div>
                            <h3 className="font-anton uppercase text-3xl text-white leading-tight">
                                Pick your <span className="text-hotpink">handle</span>
                            </h3>
                            <p className="font-chivo text-white/60 text-sm">
                                Drop a 2-24 character handle (letters, numbers, _ -).
                                Comes back automatically next time you visit.
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3">
                                <input
                                    data-testid="ledger-handle-input"
                                    value={handle}
                                    onChange={(e) => setHandle(e.target.value)}
                                    maxLength={24}
                                    placeholder="your_handle"
                                    className="bg-black border-2 border-white/15 px-4 py-3 font-chivo text-white placeholder:text-white/30 focus:border-kickgreen focus:outline-none"
                                />
                                <button
                                    data-testid="ledger-join-btn"
                                    disabled={busy === "join"}
                                    className="bg-kickgreen text-black font-anton uppercase tracking-wider text-lg px-6 py-3 border-2 border-black hover:-translate-y-0.5 hover:shadow-[6px_6px_0_#FF2E93] transition-all disabled:opacity-60"
                                >
                                    {busy === "join" ? "Joining…" : "Join · It's Free"}
                                </button>
                            </div>
                        </form>
                    ) : (
                        <div
                            data-testid="ledger-dashboard"
                            className="bg-surface border-4 border-black brutal-shadow-pink p-6 md:p-8 space-y-5"
                        >
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div>
                                    <div className="font-anton uppercase tracking-widest text-xs text-white/60">
                                        Welcome back
                                    </div>
                                    <div className="font-graffiti text-skyblue text-3xl -rotate-2 leading-none mt-1">
                                        @{fan.handle}
                                    </div>
                                </div>
                                <button
                                    data-testid="ledger-logout-btn"
                                    onClick={logout}
                                    title="Sign out of the ledger"
                                    className="inline-flex items-center gap-1 border-2 border-white/30 text-white/60 hover:text-hotpink hover:border-hotpink font-anton uppercase text-xs px-3 py-1.5 transition-colors"
                                >
                                    <LogOut className="w-3 h-3" /> Sign out
                                </button>
                            </div>

                            <div className="bg-black border-2 border-kickgreen p-5 flex items-baseline gap-3">
                                <span
                                    data-testid="ledger-points"
                                    className="font-anton text-6xl md:text-7xl text-kickgreen leading-none"
                                >
                                    {fan.points}
                                </span>
                                <span className="font-anton uppercase tracking-widest text-sm text-white/60">
                                    PTS
                                </span>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <ActionBtn
                                    testid="ledger-checkin-btn"
                                    icon={CalendarCheck}
                                    label="Daily Check-In"
                                    pts="+10"
                                    tone="green"
                                    onClick={doCheckin}
                                    busy={busy === "checkin"}
                                    done={checkinDone}
                                />
                                <ActionBtn
                                    testid="ledger-share-btn"
                                    icon={Share2}
                                    label="Share Stream"
                                    pts="+25"
                                    tone="pink"
                                    onClick={doShare}
                                    busy={busy === "share"}
                                    done={shareDone}
                                />
                            </div>
                            <p className="font-chivo text-xs text-white/40">
                                Cooldown resets at midnight UTC. Bigger rewards drop
                                soon — keep stacking.
                            </p>
                        </div>
                    )}
                </div>

                {/* Leaderboard */}
                <div className="lg:col-span-2">
                    <div
                        data-testid="leaderboard"
                        className="bg-black border-4 border-black brutal-shadow-blue p-5 md:p-6"
                    >
                        <div className="flex items-center gap-2 mb-4">
                            <Trophy className="w-5 h-5 text-skyblue" strokeWidth={2.5} />
                            <span className="font-anton uppercase tracking-widest text-sm text-white">
                                Top Advocates
                            </span>
                        </div>
                        {board.length === 0 ? (
                            <p className="font-chivo text-white/40 text-sm py-8 text-center">
                                Empty ledger. Be the first to stack.
                            </p>
                        ) : (
                            <ol className="space-y-2">
                                {board.map((row) => (
                                    <LeaderRow
                                        key={row.handle}
                                        row={row}
                                        you={fan?.handle === row.handle}
                                    />
                                ))}
                            </ol>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

const toneActionMap = {
    green: {
        cls: "bg-kickgreen text-black hover:shadow-[6px_6px_0_#FF2E93]",
        done: "border-kickgreen text-kickgreen",
    },
    pink: {
        cls: "bg-hotpink text-white hover:shadow-[6px_6px_0_#0AC3FF]",
        done: "border-hotpink text-hotpink",
    },
};

const ActionBtn = ({ testid, icon: Icon, label, pts, tone, onClick, busy, done }) => {
    if (done) {
        return (
            <div
                data-testid={`${testid}-done`}
                className={`bg-black border-2 ${toneActionMap[tone].done} p-4 flex items-center gap-3`}
            >
                <Icon className="w-5 h-5" strokeWidth={2.5} />
                <div>
                    <div className="font-anton uppercase tracking-wider text-sm">
                        {label}
                    </div>
                    <div className="font-chivo text-xs opacity-70">Claimed today ✓</div>
                </div>
            </div>
        );
    }
    return (
        <button
            data-testid={testid}
            onClick={onClick}
            disabled={busy}
            className={`${toneActionMap[tone].cls} font-anton uppercase tracking-wider text-base px-4 py-4 border-2 border-black hover:-translate-y-0.5 transition-all disabled:opacity-60 flex items-center gap-3 text-left`}
        >
            <Icon className="w-5 h-5" strokeWidth={2.5} />
            <span className="flex-1">{label}</span>
            <span className="text-xs opacity-80">{pts}</span>
        </button>
    );
};

const rankStyle = {
    1: { text: "text-kickgreen", crown: "#53FC18", size: "text-3xl" },
    2: { text: "text-skyblue", crown: "#0AC3FF", size: "text-2xl" },
    3: { text: "text-hotpink", crown: "#FF2E93", size: "text-xl" },
};

const LeaderRow = ({ row, you }) => {
    const style = rankStyle[row.rank];
    return (
        <li
            data-testid="leader-row"
            className={`flex items-center gap-3 px-3 py-2.5 border-2 ${you ? "border-kickgreen bg-kickgreen/5" : "border-white/10"} hover:border-white/30 transition-colors`}
        >
            <div className="w-10 flex items-center justify-center">
                {style ? (
                    <Crown className="w-7 h-5" color={style.crown} />
                ) : (
                    <span className="font-anton text-white/40 text-base">#{row.rank}</span>
                )}
            </div>
            <div className="flex-1 min-w-0">
                <div
                    className={`font-anton uppercase tracking-tight ${style ? style.size + " " + style.text : "text-base text-white"} leading-none truncate`}
                >
                    {row.handle}
                </div>
                {you && (
                    <div className="font-graffiti text-skyblue text-xs -rotate-2 leading-none mt-1">
                        that's you ✦
                    </div>
                )}
            </div>
            <div className="font-anton text-white text-lg whitespace-nowrap">
                {row.points}
                <span className="text-white/40 text-xs ml-1">pts</span>
            </div>
        </li>
    );
};
